<template>
  <page-layout ref="layout" @refresh="refresh">
    <template #breadcrumbs>
      <b-breadcrumb-item :text="`Reports - ${$store.state.settings.app.current.title}`" />
      <b-breadcrumb-item text="Event Students" active/>
    </template>

    <template #dropdown-options>
      <b-dropdown-item @click="refresh">
        <feather-icon icon="RotateCwIcon"/>
        <span class="align-middle ml-50">Refresh</span>
      </b-dropdown-item>
    </template>

    <template #content>
      <table-layout
        ref="table-layout2"
        :loading="table.loading"
        :fields="table.fields"
        :items="table.items"
        :sorting="table.sorting"
        :filters="table.filters"
        :filters-options="{ visible: true, collapsed: false }">

        <template #overlay>
          <overlay-loading :items="[
            { state: table.loading, desc: 'Loading Selections', loaded: table.loaded},
            { state: state.loading, desc: 'Rendering Template'},
          ]" />
        </template>

        <template #filters>
          <b-form-group label="Ensemble" label-for="ensemble-input">
            <table-filter
              id="ensemble-input"
              v-model="table.filters.ensemble.value"
              :options="filteredEnsembles.map(e => ({ label: e.name, value: e.id }))"
              :loading="ensembles.loading"
              :reduce="option => option.value"
              :select-on-tab="true"
              :clearable="true"
              class="w-100 d-print-none"/>
          </b-form-group>

          <b-form-group label="Hotel" label-for="hotel-input">
            <table-filter
              id="hotel-input"
              v-model="table.filters.hotel.value"
              :options="hotels.items.map(e => ({ label: e.name, value: e.id }))"
              :loading="hotels.loading"
              :reduce="option => option.value"
              :select-on-tab="true"
              :clearable="true"
              class="w-100 d-print-none"/>
          </b-form-group>

          <b-form-group label="Bus" label-for="bus-input">
            <table-filter
              id="bus-input"
              v-model="table.filters.bus.value"
              :options="buses.items.map(e => ({ label: e.name, value: e.id }))"
              :loading="buses.loading"
              :reduce="option => option.value"
              :select-on-tab="true"
              :clearable="true"
              class="w-100 d-print-none"/>
          </b-form-group>

          <b-form-group label="Chaperone" label-for="chaperone-input">
            <table-filter
              id="chaperone-input"
              v-model="table.filters.chaperone.value"
              :options="chaperones.items.map(e => ({ label: e.name ? `${e.name.first} ${e.name.last}` : 'Unnamed Chaperone', value: e.id }))"
              :loading="chaperones.loading"
              :reduce="option => option.value"
              :select-on-tab="true"
              :clearable="true"
              class="w-100 d-print-none"/>
          </b-form-group>

          <b-form-group label="Received Medical Form" label-for="medical-input">
            <table-filter
              id="medical-input"
              v-model="table.filters.medicalReceived.value"
              :options="[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ]"
              :reduce="option => option.value"
              :select-on-tab="true"
              :clearable="true"
              class="w-100 d-print-none"/>
          </b-form-group>

          <b-form-group label="Sex" label-for="sex-input">
            <table-filter
              id="sex-input"
              v-model="table.filters.sex.value"
              :options="[
                { label: 'Male', value: 'male' },
                { label: 'Female', value: 'female' },
              ]"
              :reduce="option => option.value"
              :select-on-tab="true"
              :clearable="true"
              class="w-100 d-print-none"/>
          </b-form-group>

          <b-form-group label="Gender" label-for="gender-input">
            <table-filter
              id="gender-input"
              v-model="table.filters.gender.value"
              :options="getGenders()"
              :reduce="option => option.value"
              :select-on-tab="true"
              :clearable="true"
              class="w-100 d-print-none"/>
          </b-form-group>

          <b-form-group label="Accepted" label-for="accepted-input">
            <table-filter
              id="accepted-input"
              v-model="table.filters.accepted.value"
              :options="[
                { label: 'Accepted', value: 'true' },
                { label: 'Undecided', value: 'null' },
              ]"
              :reduce="option => option.value"
              :select-on-tab="true"
              :clearable="true"
              class="w-100 d-print-none"/>
          </b-form-group>

        </template>

        <template #cell(student.name)="{data}">
          <div v-if="data.item.name && data.item.name.first">
            <router-link :to="{ name: 'events-student', params: { id: data.item.studentId } }">
              {{ getPersonName(data.item, { format: 'lastFirst' }) }}
            </router-link>
          </div>
        </template>

        <template #cell(name.first)="{data}">
          <div v-if="data.item.name && data.item.name.first">
            <router-link :to="{ name: 'events-student', params: { id: data.item.studentId } }">
              {{ data.item.name.first }}
            </router-link>
          </div>
        </template>
        <template #cell(name.last)="{data}">
          <div v-if="data.item.name && data.item.name.last">
            <router-link :to="{ name: 'events-student', params: { id: data.item.studentId } }">
              {{ data.item.name.last }}
            </router-link>
          </div>
        </template>
        <template #cell(name.preferred)="{data}">
          <div v-if="data.item.name && data.item.name.preferred">
            <router-link :to="{ name: 'events-student', params: { id: data.item.studentId } }">
              {{ data.item.name.preferred }}
            </router-link>
          </div>
        </template>
        <template #cell(ensemble.name)="{data}">
          <div v-if="data.item.ensemble && data.item.ensemble.name">
            <router-link v-if="$can('view', 'management-ensemble')" :to="{ name: 'management-ensemble', params: { slug: data.item.ensemble.slug } }">
              {{ data.item.ensemble.name }}
            </router-link>
            <template v-else>
              {{ data.item.ensemble.name }}
            </template>
          </div>
        </template>
        <template #cell(hotel.name)="{data}">
          <div v-if="data.item.hotel && data.item.hotel.name">
            <router-link :to="{ name: 'events-hotel', params: { id: data.item.hotel.id } }">
              {{ data.item.hotel.name }}
            </router-link>
          </div>
        </template>
        <template #cell(bus.name)="{data}">
          <div v-if="data.item.bus && data.item.bus.name">
            <router-link :to="{ name: 'events-bus', params: { id: data.item.bus.id } }">
              {{ data.item.bus.name }}
            </router-link>
          </div>
        </template>
        <template #cell(chaperone.name)="{data}">
          <div v-if="data.item.chaperone && data.item.chaperone.name">
            <router-link :to="{ name: 'events-chaperone', params: { id: data.item.chaperone.id } }">
              {{ getPersonName(data.item.chaperone, { format: 'lastFirst' }) }}
            </router-link>
          </div>
        </template>
        <template #cell(medicalReceived)="{data}">
          <div v-if="data.item.medicalReceived !== null && data.item.medicalReceived !== undefined">
            <b-badge :variant="data.item.medicalReceived ? 'success' : 'danger'">
              {{ data.item.medicalReceived ? 'Yes' : 'No' }}
            </b-badge>
          </div>
        </template>
        <template #cell(accepted)="{data}">
          <div>
            <b-badge :variant="data.item.accepted === true ? 'success' : 'secondary'">
              {{ data.item.accepted === true ? 'Accepted' : 'Undecided' }}
            </b-badge>
          </div>
        </template>

      </table-layout>
    </template>
    <template #debug>
      {{ selections }}
    </template>
  </page-layout>
</template>

<script>
  import vSelect from 'vue-select';
  import TableRowOptions from '@/components/TableRowOptions.vue';
  import TableFilter from '@/components/TableFilter.vue';
  import OverlayLoading from '@/components/OverlayLoading.vue';
  import TableLayout from '@/components/TableLayout.vue';
  import PageLayout from '@/components/PageLayout.vue';
  import lodashMixin from '@/mixins/lodash.mixin';
  import settingsMixin from '@/mixins/settings.mixin';
  import { API, graphqlOperation } from 'aws-amplify';
  import { listSelections, listStudents, listStudentEvents, listBuses, listChaperones, listEnsembles, listHotels } from './queries/event-students-report';
  import { genders } from '@/views/events/service/data';
  import personMixin from '@/mixins/person.mixin';
  
  export default {
    name: 'EventStudentsReport',
    components: {
      TableRowOptions,
      TableFilter,
      OverlayLoading,
      TableLayout,
      PageLayout,
      vSelect
    },
    mixins: [ lodashMixin, settingsMixin, personMixin],
    data() {
      return {
        selections: {
          items: [],
          loading: true
        },
        students: {
          items: [],
          loading: true
        },
        studentEvents: {
          items: [],
          loading: true
        },
        buses: {
          items: [],
          loading: true
        },
        chaperones: {
          items: [],
          loading: true
        },
        ensembles: {
          items: [],
          loading: true
        },
        hotels: {
          items: [],
          loading: true
        },
        table: {
          fields: [
            {
              key: 'student.name',
              subKeys: ['name.last', 'name.first'],
              label: 'Student',
              sortable: true,
              filterable: true,
              visible: true,
              tdClass: 'align-middle'
            },
            {
              key: 'name.last',
              label: 'Last Name',
              sortable: true,
              filterable: true,
              visible: false,
              tdClass: 'align-middle'
            },
            {
              key: 'name.first',
              label: 'First Name',
              sortable: true,
              filterable: true,
              visible: false,
              tdClass: 'align-middle'
            },
            {
              key: 'name.preferred',
              label: 'Preferred Name',
              sortable: true,
              filterable: true,
              visible: true,
              tdClass: 'align-middle'
            },
            {
              key: 'phone',
              label: 'Cell Phone',
              sortable: false,
              filterable: true,
              visible: true,
              tdClass: 'align-middle'
            },
            {
              key: 'ensemble.name',
              label: 'Ensemble',
              sortable: true,
              filterable: true,
              visible: true,
              tdClass: 'align-middle'
            },
            {
              key: 'hotel.name',
              label: 'Hotel',
              sortable: true,
              filterable: true,
              visible: true,
              tdClass: 'align-middle'
            },
            {
              key: 'room',
              label: 'Room',
              sortable: true,
              filterable: true,
              visible: true,
              tdClass: 'align-middle'
            },
            {
              key: 'bus.name',
              label: 'Bus',
              sortable: true,
              filterable: true,
              visible: true,
              tdClass: 'align-middle'
            },
            {
              key: 'chaperone.name',
              subKeys: [ 'chaperone.name.last', 'chaperone.name.first'],
              label: 'Chaperone',
              sortable: true,
              filterable: true,
              visible: true,
              tdClass: 'align-middle'
            },
            {
              key: 'medicalReceived',
              label: 'Medical Received',
              sortable: true,
              filterable: true,
              visible: false,
              tdClass: 'align-middle'
            },
            {
              key: 'sex',
              label: 'Sex',
              sortable: true,
              filterable: true,
              visible: false,
              tdClass: 'align-middle text-capitalize'
            },
            {
              key: 'gender',
              label: 'Gender',
              sortable: true,
              filterable: true,
              visible: false,
              tdClass: 'align-middle text-capitalize'
            },
            {
              key: 'accepted',
              label: 'Accepted',
              sortable: true,
              filterable: true,
              visible: false,
              tdClass: 'align-middle text-capitalize'
            },
          ],
          sorting: {
            mode: 'plan',
            plan: [
              { key: 'name.last', dir: 'asc' },
              { key: 'name.first', dir: 'asc' },
            ],
            fields: [
              { key: 'name.last', text: 'Student: Last Name', type: 'string' },
              { key: 'name.first', text: 'Student: First Name', type: 'string' },
              { key: 'name.preferred', text: 'Student: Preferred Name', type: 'number' },
              { key: 'ensemble.name', text: 'Ensemble', type: 'string' },
              { key: 'hotel.name', text: 'Hotel', type: 'string' },
              { key: 'room', text: 'Room', type: 'string' },
              { key: 'bus.name', text: 'Bus', type: 'string' },
              { key: 'chaperone.name.last', text: 'Chaperone: Last Name', type: 'string' },
              { key: 'chaperone.name.first', text: 'Chaperone: First Name', type: 'string' },
              { key: 'sex', text: 'Sex', type: 'string' },
              { key: 'gender', text: 'Gender', type: 'string' },
              { key: 'accepted', text: 'Accepted', type: 'string' },
              { key: 'medicalReceived', text: 'Medical Received', type: 'string' },
            ],
            presets: [
              {
                id: 'lnf',
                label: 'Last Name → First Name',
                plan: [
                  { key: 'name.last', dir: 'asc' },
                  { key: 'name.first', dir: 'asc' },
                ],
              },
            ]
            // by: 'name.last',
            // desc: false
          },
          search: {
            type: '\'',
            value: '',
            options: {
              types: [
                { text: 'Fuzzy', value: '' },
                { text: 'Exact', value: '=' },
                { text: 'Includes', value: '\'' },
                { text: 'Starts With', value: '^' },
              ]
            },
          },
          filters: {
            bus: { key: 'bus.id', value: null },
            chaperone: { key: 'chaperone.id', value: null },
            ensemble: { key: 'ensemble.id', value: null },
            hotel: { key: 'hotel.id', value: null },
            medicalReceived: { key: 'medicalReceived', value: null },
            sex: { key: 'sex', value: null },
            gender: { key: 'gender', value: null },
            accepted: { key: 'accepted', value: null },
          },
          items: [],
          loading: false
        },
      };
    },
    computed: {
      filteredEnsembles() {
        // Early return if ensembles aren’t loaded yet
        if (!Array.isArray(this.ensembles?.items)) return [];

        return this.ensembles.items.filter(e => {
          const name = e.name?.toLowerCase() ?? '';
          const code = e.code?.toLowerCase() ?? '';
          // Exclude anything with code 'A' or name containing 'alternate'
          return code !== 'a' && !name.includes('alternate');
        });
      },
    },
    async mounted() {
      await Promise.all([
        this.getBuses(),
        this.getChaperones(),
        this.getEnsembles(),
        this.getHotels(),
        this.getStudentEvents(),
        this.getSelections(),
        this.getStudents()
      ]);
      this.buildTable();
      this.$refs.layout.state.loading = false
    },
    methods: {
      // eslint-disable-next-line no-empty-function
      async refresh() {
        this.$refs.layout.state.loading = true
        await Promise.all([
          this.getBuses(),
          this.getChaperones(),
          this.getEnsembles(),
          this.getHotels(),
          this.getSelections(),
          this.getStudentEvents()
        ]);
        this.buildTable();
        this.$refs.layout.state.loading = false
      },
      async getSelections() {
        this.selections.loading = true
        this.selections.items = []
        let nextToken = null

        do {
          const input = {
            filter: {
              accepted: { ne: false },
              createdAt: {
                between: [
                  this.settingsStore.app.current.year.start,
                  this.settingsStore.app.current.year.end
                ]
              }
            },
            limit: 500,
            nextToken
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await API.graphql(graphqlOperation(listSelections, input))
          const { items, nextToken: newToken } = response.data.listSelections

          if(items?.length) {
            this.selections.items.push(...items)
          }
          nextToken = newToken
        }
        while (nextToken)
        this.selections.loading = false
      },
      async getStudents() {
        this.students.loading = true
        this.students.items = []
        let nextToken = null

        do {
          const input = {
            filter: {
              createdAt: {
                between: [
                  this.settingsStore.app.current.year.start,
                  this.settingsStore.app.current.year.end
                ]
              }
            },
            limit: 500,
            nextToken
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await API.graphql(graphqlOperation(listStudents, input))
          const { items, nextToken: newToken } = response.data.listStudents

          if(items?.length) {
            this.students.items.push(...items)
          }
          nextToken = newToken
        }
        while (nextToken)
        this.students.loading = false
      },
      async getStudentEvents() {
        this.studentEvents.loading = true
        this.studentEvents.items = []
        let nextToken = null

        do {
          const input = {
            filter: {
              createdAt: {
                between: [
                  this.settingsStore.app.current.year.start,
                  this.settingsStore.app.current.year.end
                ]
              }
            },
            limit: 500,
            nextToken
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await API.graphql(graphqlOperation(listStudentEvents, input))
          const { items, nextToken: newToken } = response.data.listStudentEvents

          if(items?.length) {
            this.studentEvents.items.push(...items)
          }
          nextToken = newToken
        }
        while (nextToken)
        this.studentEvents.loading = false
      },
      async getBuses() {
        this.buses.loading = true
        this.buses.items = []
        let nextToken = null

        do {
          const input = {
            filter: {
              createdAt: {
                between: [
                  this.settingsStore.app.current.year.start,
                  this.settingsStore.app.current.year.end
                ]
              }
            },
            limit: 500,
            nextToken
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await API.graphql(graphqlOperation(listBuses, input))
          const { items, nextToken: newToken } = response.data.listBuses

          if(items?.length) {
            this.buses.items.push(...items)
          }
          nextToken = newToken
        }
        while (nextToken)
        this.buses.items.sort((a, b) => a.name.localeCompare(b.name))
        this.buses.loading = false
      },
      async getChaperones() {
        this.chaperones.loading = true
        this.chaperones.items = []
        let nextToken = null

        do {
          const input = {
            filter: {
              createdAt: {
                between: [
                  this.settingsStore.app.current.year.start,
                  this.settingsStore.app.current.year.end
                ]
              }
            },
            limit: 500,
            nextToken
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await API.graphql(graphqlOperation(listChaperones, input))
          const { items, nextToken: newToken } = response.data.listChaperones

          if(items?.length) {
            this.chaperones.items.push(...items)
          }
          nextToken = newToken
        }
        while (nextToken)
        this.chaperones.items.sort((a, b) => (a?.name?.first ?? '').localeCompare(b?.name?.first ?? '') || (a?.name?.last ?? '').localeCompare(b?.name?.last ?? ''))
        this.chaperones.loading = false
      },
      async getHotels() {
        this.hotels.loading = true
        this.hotels.items = []
        let nextToken = null

        do {
          const input = {
            filter: {
              createdAt: {
                between: [
                  this.settingsStore.app.current.year.start,
                  this.settingsStore.app.current.year.end
                ]
              }
            },
            limit: 500,
            nextToken
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await API.graphql(graphqlOperation(listHotels, input))
          const { items, nextToken: newToken } = response.data.listHotels

          if(items?.length) {
            this.hotels.items.push(...items)
          }
          nextToken = newToken
        }
        while (nextToken)
        this.hotels.items.sort((a, b) => a.name.localeCompare(b.name))
        this.hotels.loading = false
      },
      async getEnsembles() {
        this.ensembles.loading = true
        this.ensembles.items = []
        let nextToken = null

        do {
          const input = {
            limit: 500,
            nextToken
          }

          // eslint-disable-next-line no-await-in-loop
          const response = await API.graphql(graphqlOperation(listEnsembles, input))
          const { items, nextToken: newToken } = response.data.listEnsembles

          if(items?.length) {
            this.ensembles.items.push(...items)
          }
          nextToken = newToken
        }
        while (nextToken)
        this.ensembles.items.sort((a, b) => a.name.localeCompare(b.name))
        this.ensembles.loading = false
      },

      buildTable() {
        const alternateIds = this.ensembles.items.filter(e => {
          const name = e.name?.toLowerCase() ?? '';
          const code = e.code?.toLowerCase() ?? '';
          return code === 'a' && name.includes('alternate');
        }).map(e => e.id);
        const filteredSelections = this.selections.items.filter(s => !alternateIds.includes(s.selectionEnsembleId));

        this.table.loading = true
        this.table.items = filteredSelections.map(selection => {
          const { application } = selection;
          const ensemble = selection?.selectionEnsembleId ? this.ensembles.items.find(e => e.id === selection.selectionEnsembleId) : null;
          const student = application?.studentApplicationsId ? this.students.items.find(s => s.id === application.studentApplicationsId) : null;
          const studentEvent = student?.eventId ? this.studentEvents.items.find(se => se.id === student.eventId) : null;
          const bus = studentEvent?.busId ? this.buses.items.find(b => b.id === studentEvent.busId) : null;
          const chaperone = studentEvent?.chaperoneId ? this.chaperones.items.find(c => c.id === studentEvent.chaperoneId) : null;
          const hotel = studentEvent?.hotelId ? this.hotels.items.find(h => h.id === studentEvent.hotelId) : null;

          return {
            id: selection.id,
            studentId: student.id,
            name: student.name,
            bus: bus,
            chaperone: chaperone,
            ensemble: ensemble,
            hotel: hotel,
            medicalReceived: studentEvent?.medicalReceived,
            phone: student?.phone?.number,
            room: studentEvent?.room,
            sex: student?.sex,
            gender: student?.gender,
            accepted: selection.accepted ?? 'null',
          }
        })
        this.table.loading = false
      },

      /** Data Utils **/
      getGenders() {
        return genders.map((g) => ({
          label: g.text, value: g.value
        }))
      },
    },
  };
</script>

<style scoped>

</style>
